import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { Middleware } from 'redux';
import { setVehicleTabs, setScenarioLoadSettled, resetFuelSaving } from '@makemydeal/dr-activities-fuel-saving';
import { bootstrapActionTypes, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { SCENARIOS_FETCH_SUCCESS, SCENARIOS_INGEST_COMPLETE } from '@makemydeal/dr-dash-store/actionTypes/multiScenarioActionTypes';
import { OFFER_SAVE_SUCCESS } from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import { setBaggageFuelEconomy } from '@makemydeal/dr-dash-store/actions/baggageFuelEconomyActionCreators';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedActions, scopedActionTypes, scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { configureFuelSaving } from './init';
import { buildVehicleTabsFromState, buildVehicleTabsFromScopedState } from './utils';

export const fuelSavingMiddleware: Middleware<unknown, StateTree> = (store) => (next) => (action: AnyFSA) => {
    // Gates the new live-update behavior below; existing save-triggered recompute stays always-on.
    // Deliberately a function, not a value: feature toggle selectors throw when read before
    // BOOTSTRAP_SUCCESS and this middleware runs for every dispatched action, so reading the toggle
    // up-front would throw for the whole pre-bootstrap window. Every call site below is guarded by
    // an action-type check that only matches post-bootstrap actions.
    const isAutoSavingInFuelSavingEnabled = () => featureToggleSelectors.isEnableAutoSavingInFuelSaving(store.getState());

    // Which scope was primary *before* this action. Needed only for the primary/scenario swap, to
    // find the outgoing primary's baggageFuelEconomy further down.
    // Read before next(action): the reducer flips __primaryScope synchronously.
    const previousPrimaryScopeId =
        action.type === scopedActionTypes.SCOPES_CHANGE_DEFAULT_SCOPE && isAutoSavingInFuelSavingEnabled()
            ? scopedStateSelectors.getPrimaryScopeId(store.getState() as unknown as RealState)
            : undefined;

    next(action);

    if (action.type === bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
        configureFuelSaving();
        const state = store.getState();
        store.dispatch(setVehicleTabs(buildVehicleTabsFromState(state)) as AnyFSA);
        return;
    }

    // Clone deep-copies the source scope's fuelSaving too; reset it so the new scope
    // doesn't inherit someone else's stale tabs.
    if (action.type === scopedActionTypes.SCOPES_CLONE_SCOPE && isAutoSavingInFuelSavingEnabled()) {
        const newScopeId = action.payload?.newScopeId;
        store.dispatch(scopedActions.createScopedAction(newScopeId, resetFuelSaving()) as AnyFSA);
        return;
    }

    // The new triggers: these are the deal edits that used to require a manual save before the
    // slideout caught up. Gating them here is what makes the whole feature toggle-controlled — with
    // the toggle off none of them recompute, leaving only the always-on triggers in the `if` below.
    const isAutoSavingTrigger =
        (action.type === 'VEHICLE_CHANGE_SUCCESS' ||
            action.type === scopedActionTypes.SCOPES_DELETE_SCOPE ||
            action.type === scopedActionTypes.SCOPES_CHANGE_DEFAULT_SCOPE) &&
        isAutoSavingInFuelSavingEnabled();

    // First 3 are the existing prod triggers (always on); the 4th adds this PR's toggle-gated ones.
    if (
        action.type === SCENARIOS_INGEST_COMPLETE ||
        action.type === SCENARIOS_FETCH_SUCCESS ||
        action.type === OFFER_SAVE_SUCCESS ||
        isAutoSavingTrigger
    ) {
        const state = store.getState();
        const realState = state as unknown as RealState;
        const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(realState);
        const vehicleTabs = buildVehicleTabsFromScopedState(state, realState, isAutoSavingInFuelSavingEnabled());
        // fuelSaving state is per-scope, so target the primary scope explicitly rather than
        // dispatching unscoped — an unscoped dispatch would land on whichever scope happens to be
        // ambiently bound, which is how the tabs ended up written to the wrong scenario.
        store.dispatch(scopedActions.createScopedAction(primaryScopeId, setVehicleTabs(vehicleTabs)) as AnyFSA);
        // Mark scenario load settled; SCENARIOS_INGEST_COMPLETE may fire for scenarios that never hydrate
        store.dispatch(scopedActions.createScopedAction(primaryScopeId, setScenarioLoadSettled(true)) as AnyFSA);

        // Deleted scope's own slice is never written again; clear it so it doesn't keep stale tabs.
        if (action.type === scopedActionTypes.SCOPES_DELETE_SCOPE) {
            const scopeToDelete = action.payload?.scopeToDelete;
            store.dispatch(scopedActions.createScopedAction(scopeToDelete, resetFuelSaving()) as AnyFSA);
        }

        // baggageFuelEconomy is per-scope too and only gets written on save; carry the old
        // primary's copy forward so the new primary has data to prefill from.
        if (
            action.type === scopedActionTypes.SCOPES_CHANGE_DEFAULT_SCOPE &&
            previousPrimaryScopeId &&
            previousPrimaryScopeId !== primaryScopeId
        ) {
            const previousPrimaryBaggage = scopedStateSelectors.getScopeStateById(realState, previousPrimaryScopeId)
                ?.baggageFuelEconomy?.payload;
            if (previousPrimaryBaggage) {
                store.dispatch(
                    scopedActions.createScopedAction(primaryScopeId, setBaggageFuelEconomy(previousPrimaryBaggage)) as AnyFSA
                );
            }
        }
        return;
    }
};
